/**
 * Permission-based authorization layer.
 *
 * Permissions describe *capabilities*, not roles.
 * The role→permission mapping + hierarchy lives here as a single source of truth
 * used by both server middleware and frontend rendering.
 */

import { UserRole } from './user.types';
import { Region } from './common.types';

// ── Permission Strings ──────────────────────────────────────────────────────

export type Permission =
  // Memberships / Applications
  | 'memberships:read'
  | 'memberships:manage'
  | 'applications:read'
  | 'applications:manage'
  | 'applications:review'      // IP expert assessment
  // SoW / Projects
  | 'sows:read'
  | 'sows:manage'
  | 'sows:create'              // SP creates SoW
  | 'sows:audit'               // Auditor-only lifecycle audit gate
  // Invoices
  | 'invoices:read'
  | 'invoices:manage'
  | 'invoices:create'          // SP creates invoice
  // Users / Invitations
  | 'users:read'
  | 'users:manage'
  | 'users:invite'
  // Email system
  | 'emails:read'
  | 'emails:manage'
  | 'email-templates:manage'
  // IP Expert specific
  | 'assessments:submit'
  | 'assessments:read'
  // Growth Coach specific
  | 'growth-coach:assess'
  // Service Provider firm
  | 'firms:read'
  | 'firms:manage'
  | 'practitioners:manage'
  // Admin settings
  | 'settings:manage'
  | 'region-settings:manage'
  // Programs
  | 'programs:read'
  | 'programs:manage'
  // Documents / PDFs
  | 'documents:read'
  // Site config (superAdmin exclusive)
  | 'site-config:manage'
  // Admin-only (superAdmin exclusive)
  | 'admins:manage';
// View As / impersonation is NOT a permission string — it is env-aware and
// gated by regionAccess.canImpersonate (requireImpersonationAccess middleware
// server-side, useAccess().canImpersonate client-side).

// ── Role → Permission Mapping ───────────────────────────────────────────────

const ROLE_PERMISSIONS: Record<string, readonly Permission[]> = {
  startupFounder: [
    'applications:read',
    'memberships:read',
    'sows:read',
    'programs:read',
    'firms:read',
    'documents:read',
  ],

  serviceProvider: [
    'sows:read',
    'sows:create',
    'invoices:read',
    'invoices:create',
    'firms:read',
    'firms:manage',
    'practitioners:manage',
    'documents:read',
  ],

  ipExpert: [
    'applications:read',
    'applications:review',
    'assessments:submit',
    'assessments:read',
    'sows:read',
    'documents:read',
  ],

  growthCoach: [
    'applications:read',
    'growth-coach:assess',
    'memberships:read',
    'documents:read',
  ],

  partnerAdmin: [
    'memberships:read',
    'memberships:manage',
    'applications:read',
    'applications:manage',
    'sows:read',
    'sows:manage',
    'invoices:read',
    'invoices:manage',
    'users:read',
    'users:manage',
    'users:invite',
    'emails:read',
    'emails:manage',
    'email-templates:manage',
    'assessments:read',
    'growth-coach:assess',
    'firms:read',
    'firms:manage',
    'practitioners:manage',
    'settings:manage',
    'programs:read',
    'programs:manage',
    'documents:read',
  ],

  // superAdmin inherits all partnerAdmin permissions + exclusive ops.
  // View As is deliberately absent — see regionAccess.canImpersonate.
  superAdmin: [
    'admins:manage',
    'region-settings:manage',
    'site-config:manage',
  ],

  // auditor's BASE set is partnerAdmin-equivalent (via hierarchy) + the audit
  // gate. Region-conditional extras (CT auditor = superAdmin equivalence) are
  // resolved by getEffectivePermissions — never by this map alone.
  auditor: [
    'sows:audit',
  ],
};

// ── Hierarchy Resolution ────────────────────────────────────────────────────

const ROLE_HIERARCHY: Partial<Record<UserRole, UserRole>> = {
  superAdmin: 'partnerAdmin',
  auditor: 'partnerAdmin',
};

/**
 * Resolve all permissions for a role, including inherited permissions.
 */
export function getPermissionsForRole(role: UserRole): Permission[] {
  const direct = ROLE_PERMISSIONS[role] ?? [];
  const parent = ROLE_HIERARCHY[role];
  if (parent) {
    const inherited = getPermissionsForRole(parent);
    // Merge and deduplicate
    return [...new Set([...inherited, ...direct])];
  }
  return [...direct];
}

/**
 * Check if a role has a specific permission.
 */
export function roleHasPermission(role: UserRole, permission: Permission): boolean {
  return getPermissionsForRole(role).includes(permission);
}

/**
 * Check if a role has ALL of the specified permissions.
 */
export function roleHasAllPermissions(role: UserRole, permissions: Permission[]): boolean {
  const resolved = getPermissionsForRole(role);
  return permissions.every(p => resolved.includes(p));
}

/**
 * Check if a role has ANY of the specified permissions.
 */
export function roleHasAnyPermission(role: UserRole, permissions: Permission[]): boolean {
  const resolved = getPermissionsForRole(role);
  return permissions.some(p => resolved.includes(p));
}

// ── Region-Aware Resolution ─────────────────────────────────────────────────
//
// The auditor role's power ceiling depends on home region:
//   communitech auditor  → full superAdmin equivalence + 'sows:audit'
//   any other auditor    → partnerAdmin equivalence (region-scoped) + 'sows:audit'
// Every other role resolves identically to getPermissionsForRole.
// Server middleware (requirePermission) and client conditional rendering must
// both resolve through these — never through the role-only functions — so the
// two sides cannot drift.

/**
 * Resolve the effective permissions for a user, accounting for
 * region-conditional roles.
 */
export function getEffectivePermissions(role: UserRole, region: Region): Permission[] {
  if (role === 'auditor' && region === 'communitech') {
    return [...new Set([...getPermissionsForRole('superAdmin'), ...getPermissionsForRole('auditor')])];
  }
  return getPermissionsForRole(role);
}

/**
 * Check if a user (role + region) has a specific permission.
 */
export function hasEffectivePermission(role: UserRole, region: Region, permission: Permission): boolean {
  return getEffectivePermissions(role, region).includes(permission);
}

/**
 * Check if a user (role + region) has ALL of the specified permissions.
 */
export function hasAllEffectivePermissions(role: UserRole, region: Region, permissions: Permission[]): boolean {
  const resolved = getEffectivePermissions(role, region);
  return permissions.every(p => resolved.includes(p));
}

/**
 * All roles whose permission set can include the given permission in at least
 * one region. Used by client route guards so route role-arrays derive from
 * this map instead of hardcoded role lists.
 */
export function getRolesWithPermission(permission: Permission): UserRole[] {
  const roles = Object.keys(ROLE_PERMISSIONS) as UserRole[];
  return roles.filter(role =>
    roleHasPermission(role, permission) ||
    (role === 'auditor' && hasEffectivePermission('auditor', 'communitech', permission))
  );
}
